import React from 'react'
import { useTranslation } from 'react-i18next'

import { LoyaltyCardStamp } from '@src/components/SingleOutlet/types'
import {
  DetailedPaymentMethod,
  Discount,
  LoyaltyCard,
} from '@src/graphql-types'
import {
  CheckoutRoute,
  useCheckoutRouter,
} from '@src/hooks/useCheckoutRouter/useCheckoutRouter'
import { useFormatCurrency } from '@src/hooks/useFormatCurrency'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { UseBasketTotalsResponseData } from '@src/hooks/useTotals/useBasketTotals'

import {
  LoyaltyCardText,
  LoyaltyCardAmountBold,
} from './CustomerLoyaltyCard.styles'
import { useBreakpoint } from '@src/hooks/useBreakpoint'
import { calculatePotentialToEarnOnOrder } from './CheckoutRewards/calculatePotentialToEarnForLoyalty'

export const LoyaltyCardInfoText: React.FC<{
  loyaltyCard:
    | (Pick<
        LoyaltyCard,
        | 'requiredStamps'
        | 'discountStrategy'
        | 'allowCashOrders'
        | 'isAvailableToday'
        | 'loyaltyCardColor'
        | 'loyaltyCardIcon'
        | 'netTotal'
      > & {
        accountsSectionTextOverride?: LoyaltyCard['accountsSectionTextOverride']
        discount: Pick<
          Discount,
          'discountAmount' | 'discountPercentage' | 'minimumSubtotalGross'
        >
      } & { loyaltyCardStamps: LoyaltyCardStamp[] })
    | null
  isTermsPage?: boolean
  isBasket?: boolean
  basketTotals?: UseBasketTotalsResponseData
  amountToSpendToEarnStamp: string
  paymentMethod?: DetailedPaymentMethod
  applyAccountsSectionTextOverride?: boolean
}> = ({
  loyaltyCard,
  isTermsPage = false,
  basketTotals,
  amountToSpendToEarnStamp,
  paymentMethod,
  isBasket = false,
  applyAccountsSectionTextOverride = false,
}) => {
  const { t } = useTranslation('customerLoyaltyCard')
  const checkout = useCheckoutRouter()
  const marketplace = useMarketplace()
  const formatCurrency = useFormatCurrency()
  const { isMobile } = useBreakpoint()

  if (!loyaltyCard) {
    return null
  }

  const loyaltyCardStamps = loyaltyCard.loyaltyCardStamps || []
  const isLoyaltyCardFull =
    loyaltyCard.requiredStamps === loyaltyCardStamps.length

  const amountSavedFromStampTotals = loyaltyCard.netTotal
    ? formatCurrency(loyaltyCard.netTotal)
    : formatCurrency(0)

  const potentialToEarnOnOrder = calculatePotentialToEarnOnOrder(
    basketTotals,
    loyaltyCard
  )

  const genericLoyaltyCardText = () => {
    if (
      applyAccountsSectionTextOverride &&
      loyaltyCard.accountsSectionTextOverride
    ) {
      return (
        <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
          {loyaltyCard.accountsSectionTextOverride}
        </LoyaltyCardText>
      )
    }
    if (!loyaltyCard.isAvailableToday) {
      return (
        <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
          {t('unavailable_today')}
        </LoyaltyCardText>
      )
    }
    if (
      amountSavedFromStampTotals !== formatCurrency(0) &&
      !isLoyaltyCardFull
    ) {
      return (
        <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
          {t('you_have_collected')}{' '}
          <LoyaltyCardAmountBold isBasket={isBasket}>
            {t('amount_saved', { amountSaved: amountSavedFromStampTotals })}
          </LoyaltyCardAmountBold>{' '}
          {!isMobile && t('so_far')}
        </LoyaltyCardText>
      )
    }

    if (isLoyaltyCardFull) {
      if (isMobile) {
        return (
          <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
            {t('ready_to_collect_short')}{' '}
            <LoyaltyCardAmountBold isBasket={isBasket}>
              {t('amount_saved', { amountSaved: amountSavedFromStampTotals })}
            </LoyaltyCardAmountBold>{' '}
          </LoyaltyCardText>
        )
      } else
        return (
          <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
            {t('ready_to_collect')}{' '}
            <LoyaltyCardAmountBold isBasket={isBasket}>
              {t('amount_saved', { amountSaved: amountSavedFromStampTotals })}
            </LoyaltyCardAmountBold>{' '}
            {t('reward')}
          </LoyaltyCardText>
        )
    }

    return (
      <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
        {t('start_collecting_today')}
      </LoyaltyCardText>
    )
  }

  const termsAndConditionsLoyaltyText = () => {
    if (!loyaltyCard.isAvailableToday) {
      return (
        <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
          {t('card_unavailable_today')}
        </LoyaltyCardText>
      )
    }
    if (
      amountSavedFromStampTotals !== formatCurrency(0) &&
      !isLoyaltyCardFull
    ) {
      return (
        <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
          {t('you_have_collected')}{' '}
          <LoyaltyCardAmountBold isBasket={isBasket}>
            {t('amount_saved', { amountSaved: amountSavedFromStampTotals })}
          </LoyaltyCardAmountBold>{' '}
          {t('so_far')}
        </LoyaltyCardText>
      )
    }

    if (isLoyaltyCardFull) {
      return (
        <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
          {t('ready_to_collect')} {t('reward')}{' '}
          <LoyaltyCardAmountBold isBasket={isBasket}>
            {t('amount_saved', { amountSaved: amountSavedFromStampTotals })}
          </LoyaltyCardAmountBold>{' '}
          {t('will_be_discounted')}
        </LoyaltyCardText>
      )
    }

    return (
      <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
        {t('start_collecting_today_extra_info', {
          businessOrRestaurant:
            marketplace.urlPath === 'takeaways' ? 'restaurants' : 'businesses',
        })}
      </LoyaltyCardText>
    )
  }

  const basketSummaryText = () => {
    if (amountToSpendToEarnStamp !== formatCurrency(0)) {
      return (
        <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
          {t('add')}{' '}
          <LoyaltyCardAmountBold isBasket={isBasket}>
            {amountToSpendToEarnStamp}
          </LoyaltyCardAmountBold>{' '}
          {isMobile
            ? t('more_to_earn_a_stamp_short')
            : t('more_to_earn_a_stamp')}
        </LoyaltyCardText>
      )
    }

    if (!isLoyaltyCardFull && loyaltyCardStamps.length !== 0) {
      return (
        <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
          {t('earn')}{' '}
          <LoyaltyCardAmountBold isBasket={isBasket}>
            {t('potential_to_earn', {
              potentialToEarn: formatCurrency(potentialToEarnOnOrder),
            })}
          </LoyaltyCardAmountBold>{' '}
        </LoyaltyCardText>
      )
    }

    if (isLoyaltyCardFull) {
      if (isMobile) {
        return (
          <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
            {t('ready_to_collect_short')}{' '}
            <LoyaltyCardAmountBold isBasket={isBasket}>
              {t('amount_saved', { amountSaved: amountSavedFromStampTotals })}
            </LoyaltyCardAmountBold>{' '}
          </LoyaltyCardText>
        )
      } else
        return (
          <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
            {t('ready_to_collect')}{' '}
            <LoyaltyCardAmountBold isBasket={isBasket}>
              {t('amount_saved', { amountSaved: amountSavedFromStampTotals })}
            </LoyaltyCardAmountBold>{' '}
            {t('reward')}
          </LoyaltyCardText>
        )
    }

    if (!loyaltyCardStamps.length && potentialToEarnOnOrder > 0) {
      return (
        <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
          {t('earn')}{' '}
          <LoyaltyCardAmountBold isBasket={isBasket}>
            {t('potential_to_earn', {
              potentialToEarn: formatCurrency(potentialToEarnOnOrder),
            })}
          </LoyaltyCardAmountBold>{' '}
        </LoyaltyCardText>
      )
    }

    return (
      <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
        {t('start_collecting_today')}
      </LoyaltyCardText>
    )
  }

  const checkoutText = () => {
    if (amountToSpendToEarnStamp !== formatCurrency(0)) {
      return (
        <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
          {t('add')}{' '}
          <LoyaltyCardAmountBold isBasket={isBasket}>
            {amountToSpendToEarnStamp}
          </LoyaltyCardAmountBold>{' '}
        </LoyaltyCardText>
      )
    }
    if (
      !loyaltyCard.allowCashOrders &&
      paymentMethod === DetailedPaymentMethod.CASH
    ) {
      return (
        <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
          {t('cash_orders_not_accepted')}
        </LoyaltyCardText>
      )
    }
    if (!isLoyaltyCardFull) {
      return (
        <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
          {t('you_are_earning')}{' '}
          <LoyaltyCardAmountBold isBasket={isBasket}>
            {t('potential_to_earn', {
              potentialToEarn: formatCurrency(potentialToEarnOnOrder),
            })}
          </LoyaltyCardAmountBold>
        </LoyaltyCardText>
      )
    }

    if (isLoyaltyCardFull) {
      if (isMobile) {
        return (
          <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
            {t('ready_to_collect_short')}{' '}
            <LoyaltyCardAmountBold isBasket={isBasket}>
              {t('amount_saved', { amountSaved: amountSavedFromStampTotals })}
            </LoyaltyCardAmountBold>
            {'!'}
          </LoyaltyCardText>
        )
      } else
        return (
          <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
            {t('ready_to_collect')}{' '}
            <LoyaltyCardAmountBold isBasket={isBasket}>
              {t('amount_saved', { amountSaved: amountSavedFromStampTotals })}
            </LoyaltyCardAmountBold>{' '}
            {t('reward')}
          </LoyaltyCardText>
        )
    }

    if (!loyaltyCardStamps.length) {
      return (
        <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
          {t('you_are_earning')}{' '}
          <LoyaltyCardAmountBold isBasket={isBasket}>
            {t('potential_to_earn', {
              potentialToEarn: formatCurrency(potentialToEarnOnOrder),
            })}
          </LoyaltyCardAmountBold>{' '}
        </LoyaltyCardText>
      )
    }

    return (
      <LoyaltyCardText isTermsPage={isTermsPage} isBasket={isBasket}>
        {t('start_collecting_today')}
      </LoyaltyCardText>
    )
  }

  if (!loyaltyCard.isAvailableToday) {
    return genericLoyaltyCardText()
  }

  if (basketTotals) {
    if (
      checkout.route === CheckoutRoute.BASKET ||
      checkout.route === undefined
    ) {
      return basketSummaryText()
    } else {
      return checkoutText()
    }
  }

  if (isTermsPage) {
    return termsAndConditionsLoyaltyText()
  }

  return genericLoyaltyCardText()
}
